import { Link } from "react-router-dom";
import { Navigation } from "@/components/ui/navigation";
import { Button } from "@/components/ui/button";
import { 
  BookOpen, 
  Brain, 
  Target, 
  Sparkles,
  CheckCircle2,
  Users,
  GraduationCap,
  FileText
} from "lucide-react";

const features = [
  {
    icon: FileText,
    title: "40,000+ Practice Questions",
    description: "Across 22 AP subjects"
  },
  {
    icon: BookOpen,
    title: "Complete Notes",
    description: "Every unit and lesson covered"
  },
  {
    icon: Target,
    title: "Personalized Learning",
    description: "Focus on your weak areas"
  },
  {
    icon: Sparkles,
    title: "100% Free",
    description: "No paywalls or subscriptions"
  }
];

const trustPoints = [
  "Built using official AP course frameworks",
  "Designed around how students actually study",
  "Used by thousands of students preparing for exams",
  "Created from firsthand AP exam experience"
];

export default function About() {
  return (
    <div className="min-h-screen bg-soft-gradient text-neutral-900">
      <Navigation />
      
      <main className="flex flex-col">
        {/* Section 1: Hero / Intro */}
        <section className="relative pt-12 pb-16 sm:pt-16 sm:pb-20">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            {/* Optional: Simple icon */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10">
                <GraduationCap className="h-8 w-8 text-primary" />
              </div>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Built by Students.<br />Designed for AP Success.
            </h1>
            
            <p className="mt-4 text-lg sm:text-xl text-neutral-600 max-w-2xl mx-auto">
              Created by AP students who know the struggle—making high-quality AP prep free and accessible to everyone.
            </p>
          </div>
        </section>

        {/* Section 2: Our Mission */}
        <section className="py-12 sm:py-16 bg-white/50">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-center mb-6">
              Our Mission
            </h2>
            
            <div className="space-y-4 text-neutral-700 text-center">
              <p className="text-lg">
                AP prep shouldn't cost hundreds of dollars.
              </p>
              <p className="text-lg">
                It shouldn't feel overwhelming or inaccessible.
              </p>
              <p className="text-lg font-medium text-neutral-900">
                We built this platform to make free, effective, and personalized AP prep available to every student—no matter their background.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: What We Built (Key Features) */}
        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-center mb-10">
              What We Built
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="relative rounded-2xl border border-neutral-200 bg-white/90 backdrop-blur-sm p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mb-4">
                      <feature.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-base font-bold text-neutral-900">
                      {feature.title}
                    </h3>
                    <p className="mt-1 text-sm text-neutral-600">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 4: Who We Are */}
        <section className="py-12 sm:py-16 bg-white/50">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-center mb-8">
              Who We Are
            </h2>
            
            <div className="text-center space-y-4">
              <p className="text-lg text-neutral-700">
                We're high school students who've been through the AP grind ourselves.
              </p>
              <p className="text-lg text-neutral-700">
                We know the stress of exam prep, the frustration of expensive resources, and the feeling of not knowing where to start.
              </p>
              <p className="text-lg text-neutral-700">
                That's exactly why we built this.
              </p>
            </div>
            
            {/* Founders */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-8">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-3">
                  <span className="text-xl font-bold text-primary">SK</span>
                </div>
                <p className="font-semibold text-neutral-900">Seri Kanj</p>
                <p className="text-sm text-neutral-600">Co-Founder</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-3">
                  <span className="text-xl font-bold text-primary">KA</span>
                </div>
                <p className="font-semibold text-neutral-900">Khaled Adib</p>
                <p className="text-sm text-neutral-600">Co-Founder</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: Why Trust This Platform */}
        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-center mb-8">
              Why Students Use This
            </h2>
            
            <div className="space-y-4">
              {trustPoints.map((point, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white/80 border border-neutral-100"
                >
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-800">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 6: Call to Action */}
        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl bg-primary/5 border border-primary/10 p-8 sm:p-12 text-center">
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3">
                Start studying smarter — for free.
              </h2>
              <p className="text-neutral-600 mb-6">
                Join thousands of students preparing for their AP exams the smart way.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button asChild size="lg" className="rounded-full px-8">
                  <Link to="/auth">Get Started</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="rounded-full px-8">
                  <Link to="/subjects">Explore Subjects</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer role="contentinfo" className="mt-10 border-t border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-sm text-neutral-500">
            <p>© 2026 Lucen — All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}